import React, { useEffect, useRef, useState } from 'react'
import { Play, Pause, RotateCcw, AlertCircle, Loader } from 'lucide-react'

interface GeoGebraSimulationProps {
    filename: string
    title?: string
    description?: string
    width?: number | string
    height?: number | string
    showCustomControls?: boolean
    onReady?: (applet: any) => void
    onError?: (error: Error) => void
}

/**
 * Singleton Loader để đảm bảo library chỉ load 1 lần
 */
const loadGeoGebraLibrary = (): Promise<void> => {
    if ((window as any).GGBApplet) return Promise.resolve()

    return new Promise((resolve, reject) => {
        const script = document.createElement('script')
        script.src = '/geogebra/deployggb.js'
        script.async = true
        script.onload = () => resolve()
        script.onerror = () => reject(new Error('Cannot load GeoGebra library'))
        document.head.appendChild(script)
    })
}

const GeoGebraSimulation: React.FC<GeoGebraSimulationProps> = ({
    filename,
    title = 'Mô phỏng GeoGebra',
    description = '',
    width = '100%',
    height = 600,
    showCustomControls = true,
    onReady,
    onError
}) => {
    const containerRef = useRef<HTMLDivElement>(null)
    const appletRef = useRef<any>(null) // GeoGebra API Instance
    const uniqueId = useRef(`ggb-${Math.random().toString(36).substr(2, 9)}`)

    const [loading, setLoading] = useState(true)
    const [error, setError] = useState<string | null>(null)
    const [isPlaying, setIsPlaying] = useState(false)

    useEffect(() => {
        let mounted = true

        const initApplet = async () => {
            if (!containerRef.current) return

            try {
                await loadGeoGebraLibrary()

                if (!mounted) return

                // Calculate dimensions
                const containerWidth = containerRef.current.offsetWidth || 800
                const responsiveHeight = Math.min(
                    typeof height === 'number' ? height : 600,
                    window.innerHeight * 0.8
                )

                const params = {
                    appName: 'classic',
                    width: containerWidth,
                    height: responsiveHeight,
                    showToolBar: false,
                    showAlgebraInput: false,
                    showMenuBar: false,
                    showResetIcon: true,
                    showErrorDialogs: false,
                    showFullscreenButton: true,
                    scaleContainerClass: uniqueId.current,
                    allowUpscale: true,
                    filename: filename,
                    // QUAN TRỌNG: Sử dụng callback cục bộ thay vì global
                    appletOnLoad: (api: any) => {
                        console.log(`✅ GeoGebra ${uniqueId.current} Ready`)
                        if (mounted) {
                            setLoading(false)
                            appletRef.current = api
                            if (onReady) onReady(api)
                        }
                    }
                }

                const GGBApplet = (window as any).GGBApplet
                const applet = new GGBApplet(params, true)

                // Inject
                applet.inject(uniqueId.current)

            } catch (err) {
                console.error(err)
                if (mounted) {
                    setError('Không thể tải thư viện GeoGebra')
                    setLoading(false)
                }
            }
        }

        initApplet()

        return () => {
            mounted = false
            // Lưu ý: GeoGebra API cũ không có phương thức destroy hoàn toàn sạch sẽ cho DOM elements,
            // nhưng React sẽ remove container div khi unmount.
        }
    }, [filename, height])

    // -- CONTROLS --
    const handlePlay = () => {
        if (appletRef.current) {
            appletRef.current.startAnimation()
            setIsPlaying(true)
        }
    }

    const handlePause = () => {
        if (appletRef.current) {
            appletRef.current.stopAnimation()
            setIsPlaying(false)
        }
    }

    const handleReset = () => {
        if (appletRef.current) {
            appletRef.current.reset()
            setIsPlaying(false)
        }
    }

    return (
        <div className="geogebra-simulation bg-white rounded-xl border border-gray-200 overflow-hidden shadow-sm">
            {/* Header */}
            {title && (
                <div className="bg-gradient-to-r from-green-500 to-teal-600 text-white px-4 py-3">
                    <h3 className="text-lg font-semibold">{title}</h3>
                    {description && (
                        <p className="text-sm text-green-50 mt-1">{description}</p>
                    )}
                </div>
            )}

            {/* Container */}
            <div className="relative bg-gray-100">
                {loading && (
                    <div className="absolute inset-0 z-10 flex items-center justify-center bg-gray-50/90">
                        <div className="text-center">
                            <Loader className="w-8 h-8 text-green-600 animate-spin mx-auto mb-2" />
                            <p className="text-sm text-gray-500">Đang tải...</p>
                        </div>
                    </div>
                )}

                {error && (
                    <div className="absolute inset-0 z-10 flex items-center justify-center bg-red-50">
                        <div className="text-center text-red-600">
                            <AlertCircle className="w-8 h-8 mx-auto mb-2" />
                            <p>{error}</p>
                        </div>
                    </div>
                )}

                <div
                    id={uniqueId.current}
                    ref={containerRef}
                    className={`ggb-container ${uniqueId.current}`} // Class riêng để scale
                    style={{ minHeight: '500px' }}
                />
            </div>

            {/* Custom Controls (Chỉ hiện khi ready) */}
            {showCustomControls && !loading && !error && (
                <div className="bg-gray-50 px-4 py-3 border-t border-gray-200 flex justify-center gap-3">
                    <button onClick={isPlaying ? handlePause : handlePlay} className="flex items-center gap-2 px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg transition-colors">
                        {isPlaying ? <><Pause className="w-4 h-4" /> Tạm dừng</> : <><Play className="w-4 h-4" /> Chạy</>}
                    </button>
                    <button onClick={handleReset} className="flex items-center gap-2 px-4 py-2 bg-gray-600 hover:bg-gray-700 text-white rounded-lg transition-colors">
                        <RotateCcw className="w-4 h-4" /> Đặt lại
                    </button>
                </div>
            )}
        </div>
    )
}

export default GeoGebraSimulation
